<script>
  import { Button } from 'carbon-components-svelte';

  import Close24 from 'carbon-icons-svelte/lib/Close24';
  import Json24 from 'carbon-icons-svelte/lib/Json24';
  import Csv24 from 'carbon-icons-svelte/lib/Csv24';

  import EntityOutput from './EntityOutput.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';

  import { searchStore, updateKeys } from '../../stores';
  import { asOptionalTextColumn, downloadCsvStringAsFile, downloadJsonAsFile } from '../../utils';
  import { toCsv } from '../../../shared/csv';

  const userHeaders = [
    { key: 'name', value: 'Name' },
    { key: 'email', value: 'Email' },
    {
      key: 'organizationName',
      value: 'Organization Name',
      ...asOptionalTextColumn,
    },
    {
      key: 'groupNames',
      value: 'Group Names',
      ...asOptionalTextColumn,
    },
    { key: 'actions', value: '' },
  ];

  const groupHeaders = [
    { key: 'name', value: 'Name' },
    { key: 'atfUsername', value: 'ATF Username', ...asOptionalTextColumn },
    { key: 'organization.name', value: 'Organization' },
    { key: 'actions', value: '' },
  ];

  const organizationHeaders = [
    { key: 'name', value: 'Name' },
    { key: 'actions', empty: true, sort: false },
  ];

  const sharedCsvColumns = [
    { header: 'ID', path: 'id' },
    { header: 'Created At', path: 'createdAt' },
    { header: 'Updated At', path: 'updatedAt' },
  ];

  const entityExportConfig = [
    {
      key: 'users',
      filename: 'mace-search-output-users',
      csvColumns: [
        { header: 'Name', path: 'name' },
        { header: 'Email', path: 'email' },
        { header: 'Organization Name', path: 'organizationName' },
        { header: 'Group Names', path: 'groupNames' },
        { header: 'Status', path: 'statusDescription' },
        { header: 'Enabled', path: 'isEnabled' },
        { header: 'Email Verified', path: 'emailVerified' },
        ...sharedCsvColumns,
      ],
    },
    {
      key: 'groups',
      filename: 'mace-search-output-groups',
      csvColumns: [
        { header: 'Name', path: 'name' },
        { header: 'Short Name', path: 'shortName' },
        { header: 'Organization', path: 'organization.name' },
        { header: 'ATF Username', path: 'atfUsername' },
        { header: 'AWS IAM Role Name', path: 'awsIamRoleName' },
        { header: 'Bucket Access', path: 'bucketAccess' },
        ...sharedCsvColumns,
      ],
    },
    {
      key: 'organizations',
      filename: 'mace-search-output-organizations',
      csvColumns: [{ header: 'Name', path: 'name' }, { header: 'Short Name', path: 'shortName' }, ...sharedCsvColumns],
    },
  ];

  const getDownloadTimestamp = () => new Date().toISOString().slice(0, 19);

  function downloadCurrentSearchOutputJson() {
    downloadJsonAsFile(`mace-search-output-${getDownloadTimestamp()}`, $searchStore.filteredData);
  }

  function downloadCurrentSearchOutputCsv() {
    entityExportConfig.forEach(({ key, filename, csvColumns }) => {
      const rows = $searchStore.filteredData?.[key] ?? [];
      if (rows.length === 0) {
        return;
      }

      downloadCsvStringAsFile(`${filename}-${getDownloadTimestamp()}`, toCsv(rows, csvColumns));
    });
  }

  $: entityCount = Object.values($searchStore.filteredData ?? {}).reduce(
    (count, entityList) => count + ((entityList?.length ?? 0) > 0 ? 1 : 0),
    0
  );
</script>

<div class="close">
  <Button
    icon={Close24}
    kind="ghost"
    on:click={() => updateKeys({ overlayHidden: true })}
    title="Close Search Output Overlay"
    hideTooltip
  />
</div>
{#if $searchStore.loading}
  <LoadingSpinner spinnerDelay={0} withOverlay={false}>
    <p slot="description">Loading search results...</p>
  </LoadingSpinner>
{:else}
  <div class="header">
    <h1 class="header-title">Search</h1>
    {#if !$searchStore.loading}
      <Button
        tooltipAlignment="start"
        icon={Json24}
        kind="ghost"
        on:click={downloadCurrentSearchOutputJson}
        iconDescription="Download Current as JSON"
        portalTooltip
      />
      <Button
        tooltipAlignment="end"
        icon={Csv24}
        kind="ghost"
        on:click={downloadCurrentSearchOutputCsv}
        iconDescription={`Download Current as CSV (${entityCount})`}
        portalTooltip
      />
    {/if}
  </div>
  <EntityOutput data={$searchStore.filteredData?.users} title="Users" headers={userHeaders} routeGenerator={({ id }) => `/user/${id}`} />
  <EntityOutput
    data={$searchStore.filteredData?.groups}
    title="Groups"
    headers={groupHeaders}
    routeGenerator={({ id }) => `/group/${id}`}
  />
  <EntityOutput
    data={$searchStore.filteredData?.organizations}
    title="Organizations"
    headers={organizationHeaders}
    routeGenerator={({ id }) => `/organization/${id}`}
  />
{/if}

<style>
  .header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-variant: petite-caps;
  }

  .header-title {
    display: inline-block;
    font-weight: 600;
    font-variant: petite-caps;
  }

  .close {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
</style>
